import { useState, useEffect } from "react";
import { Reveal } from "../components/reveal";

const MARQUEE_ONE = [
  "One tier",
  "No hierarchy",
  "The room itself",
  "Peer sessions",
  "Masterminds",
  "CPD recognised",
  "Founding cohort",
  "The library",
];

const MARQUEE_TWO = [
  "Founding places are limited",
  "Locked for as long as you stay",
  "First in line",
];

const ITEM_TONES = [
  { text: "text-paper", dot: "bg-brand-pink-soft" },
  { text: "text-brand-yellow", dot: "bg-brand-yellow" },
  { text: "text-brand-pink-soft", dot: "bg-brand-blue-soft" },
  { text: "text-brand-blue-soft", dot: "bg-paper" },
];

function Marquee({ items, tall, duration }) {
  const doubled = [...items, ...items];
  return (
    <div
      aria-hidden="true"
      className={`overflow-hidden whitespace-nowrap border-y border-rule-dark bg-carbon ${
        tall ? "py-[30px]" : "py-5"
      }`}
    >
      <div className="mq-track" style={{ animationDuration: duration }}>
        {doubled.map((item, i) => {
          const tone = ITEM_TONES[i % ITEM_TONES.length];

          return (
            <span
              key={`${item}-${i}`}
              className={`disp inline-flex items-center gap-[26px] px-[26px] text-[clamp(26px,3.6vw,44px)] ${tone.text}`}
            >
              {item}
              <i className={`block h-[13px] w-[13px] shrink-0 rounded-full ${tone.dot}`} />
            </span>
          );
        })}
      </div>
    </div>
  );
}

function Chip({ num, name, tone }) {
  return (
    <div className={`mb-[34px] flex items-center gap-[14px] ${tone}`}>
      <span className="eyebrow border-2 border-current px-[9px] py-1 leading-none tracking-[0.1em]">
        {num}
      </span>
      <span className="eyebrow">{name}</span>
    </div>
  );
}

function WaitlistButton({ dark, label }) {
  return (
    <button
      type="submit"
      className={`group relative block w-full cursor-pointer overflow-hidden p-5 text-[17px] font-extrabold uppercase tracking-[0.05em] transition-transform duration-200 hover:-translate-y-0.5 ${
        dark ? "bg-brand-pink-soft text-carbon" : "bg-brand-pink text-paper"
      }`}
      style={{ fontFamily: "var(--font-display)" }}
    >
      <span
        className={`absolute inset-0 translate-y-full transition-transform duration-300 ease-out group-hover:translate-y-0 ${
          dark ? "bg-brand-yellow" : "bg-carbon"
        }`}
      />
      <span className="relative z-1">{label}</span>
    </button>
  );
}

const ZOHO_ACTION =
  "https://forms.zohopublic.com/awardsinternational/form/FreeTrialRequest/formperma/grtJxqGXh6UtE6Q_3oCe02cFGQHOKy17-NwpCXwr-H8/htmlRecords/submit";

function WaitlistForm({ compact, dark }) {
  const [done, setDone] = useState(false);

  if (compact) {
    return (
      <form
        className="mt-[42px] max-w-[430px]"
        onSubmit={(e) => {
          e.preventDefault();
          setDone(true);
        }}
      >
        <WaitlistButton dark label={done ? "You're on the list ✓" : "Join the Waitlist →"} />
      </form>
    );
  }

  return (
    <form
      action={ZOHO_ACTION}
      method="POST"
      acceptCharset="UTF-8"
      encType="multipart/form-data"
    >
      {/* Zoho tracking / redirect fields — do not rename */}
      <input type="hidden" name="zf_referrer_name" value="" />
      <input type="hidden" name="zf_redirect_url" value="" />
      <input type="hidden" name="zc_gad" value="" />
      {[
        { id: "fn", name: "Name_First", label: "First name", type: "text", ph: "Enter your first name" },
        { id: "ln", name: "Name_Last", label: "Last name", type: "text", ph: "Enter your last name" },
        { id: "co", name: "SingleLine", label: "Company", type: "text", ph: "Enter your company" },
        { id: "em", name: "Email", label: "Email address", type: "email", ph: "Enter your email address" },
      ].map((f) => {
        const fieldId = `${dark ? "final" : "top"}-${f.id}`;
        return (
          <div key={fieldId} className="mb-[18px]">
            <label
              htmlFor={fieldId}
              className={`eyebrow mb-[7px] block text-[11px] tracking-[0.16em] ${dark ? "text-paper" : "text-foreground"}`}
            >
              {f.label}
            </label>
            <input
              id={fieldId}
              name={f.name}
              type={f.type}
              maxLength={255}
              required
              placeholder={f.ph}
              className={`w-full border-2 px-4 py-[15px] text-[17px] focus:outline-3 focus:outline-offset-2 focus:outline-brand-blue ${
                dark
                  ? "border-paper bg-carbon text-paper placeholder:text-muted-dark"
                  : "border-carbon bg-card text-foreground placeholder:text-muted-foreground/70"
              }`}
            />
          </div>
        );
      })}
      <WaitlistButton dark={dark ?? false} label="Join the Waitlist →" />

      <p
        className={`mt-4 text-[15px] leading-relaxed ${dark ? "text-muted-dark" : "text-muted-foreground"}`}
      >
        We'll email you updates about this membership — the reveal, the Founding Member offer, and
        the launch. Nothing else. Unsubscribe any time, in one click. We will never share or sell
        your details. See our{" "}
        <a href="/privacy-policy" className="underline">
          privacy policy
        </a>
        .
      </p>
    </form>
  );
}

const BENEFITS = [
  {
    tag: "The Club",
    tone: "text-brand-pink-ink",
    swatch: "bg-brand-pink",
    bar: "bg-brand-pink",
    bold: "One membership. One community.",
    rest: " No tiers. Everyone in the room is in the same room.",
  },
  {
    tag: "Learning",
    tone: "text-brand-blue",
    swatch: "bg-brand-blue",
    bar: "bg-brand-blue",
    bold: "Live peer learning and closed-door mastermind sessions",
    rest: " — small groups, senior practitioners, real problems.",
  },
  {
    tag: "Learning",
    tone: "text-brand-blue",
    swatch: "bg-brand-blue",
    bar: "bg-brand-blue",
    bold: "A growing library of practical resource",
    rest: " — built by people who have actually done the work, not repackaged theory.",
  },
  {
    tag: "The Club",
    tone: "text-brand-pink-ink",
    swatch: "bg-brand-pink",
    bar: "bg-brand-pink",
    bold: "A curated global directory of experience leaders worth knowing",
    rest: " — not just people who clicked join.",
  },
  {
    tag: "Recognition",
    tone: "text-brand-yellow-ink",
    swatch: "bg-brand-yellow",
    bar: "bg-brand-yellow",
    bold: "CPD-recognised learning",
    rest: " you can put against your professional development record.",
  },
  {
    tag: "Recognition",
    tone: "text-brand-yellow-ink",
    swatch: "bg-brand-yellow",
    bar: "bg-brand-yellow",
    bold: "Founding Member places available. Limited.",
    rest: " Priced to reward the people who are in from the beginning — and locked at that price for as long as you stay.",
  },
];

const AUDIENCE = [
  "CX and EX professionals motivated by growth, building their influence, cultivating relationships and developing their professional standing",
  "Consultants, advisors and strategists working at the leading edge of experience",
  "Experience leaders inside organisations who are shaping customer and colleague experience",
  "Business leaders working across the new landscape of experience, technology and service",
  "People who work, speak, write, judge or otherwise contribute to the experience profession — and want somewhere serious to do it from, with an expert community to do it with",
];

export default function ExperienceLeadersClub() {

  useEffect(() => {
    window.location.replace("https://member.awardsinternational.com/");
  }, []);

  return null;
  
}